import { DomOverlay } from './DomOverlay';
import { Haptics } from '../Haptics';

export interface DiscoveryModalData {
  name: string;
  lore: string;
  tierLabel: string;   // e.g. "T4" or "Gem"
  familyLabel: string; // e.g. "Fire"
  familyColor: number; // 0xRRGGBB
  textureKey?: string; // Phaser texture key — pulled from canvas if rendered
  rewardShards?: number;
  rewardXp?: number;
}

type DiscoveryEvents = {
  dismiss: void;
};

/**
 * DiscoveryModalOverlay — full-screen celebration for first-time element
 * discoveries. Replaces the in-canvas Phaser toast for first-discovery
 * feedback so the moment reads as a genuine *event* rather than a blink.
 *
 * Auto-dismisses after ~3s if the player doesn't tap the CTA, so the board
 * isn't locked if they miss it.
 */
export class DiscoveryModalOverlay extends DomOverlay<DiscoveryEvents> {
  private data: DiscoveryModalData;
  private autoDismissTimer: number | null = null;

  constructor(data: DiscoveryModalData) {
    super();
    this.data = data;
    this.rootEl.className = 'dom-overlay discovery-modal-root';
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.appendChild(this.buildContent());
    this.injectStyles();
    Haptics.success();
    this.autoDismissTimer = window.setTimeout(() => this.dismiss(), 3200);
  }

  private buildContent(): HTMLDivElement {
    const backdrop = this.el('div', 'discovery-backdrop');
    backdrop.addEventListener('click', () => this.dismiss());

    const colorHex = this.toHex(this.data.familyColor);

    const panel = this.el('div', 'discovery-panel glass-panel');
    panel.style.setProperty('--discovery-color', colorHex);
    // Burst ring animation
    panel.appendChild(this.el('div', 'discovery-burst'));
    panel.appendChild(this.el('div', 'discovery-burst discovery-burst--lag'));

    panel.appendChild(this.el('div', 'discovery-kicker mono-label', 'New Discovery'));

    const glyphWrap = this.el('div', 'discovery-glyph');
    glyphWrap.textContent = this.data.name.charAt(0).toUpperCase();
    panel.appendChild(glyphWrap);

    panel.appendChild(this.el('h2', 'discovery-name type-display', this.data.name));

    const meta = this.el('div', 'discovery-meta mono-label');
    meta.textContent = `${this.data.familyLabel} · ${this.data.tierLabel}`;
    panel.appendChild(meta);

    const lore = this.el('p', 'discovery-lore', this.data.lore);
    panel.appendChild(lore);

    if (this.data.rewardShards || this.data.rewardXp) {
      const rewards = this.el('div', 'discovery-rewards');
      if (this.data.rewardXp) {
        rewards.appendChild(this.rewardChip('XP', `+${this.data.rewardXp}`));
      }
      if (this.data.rewardShards) {
        rewards.appendChild(this.rewardChip('Shards', `+${this.data.rewardShards}`));
      }
      panel.appendChild(rewards);
    }

    const cta = this.el('button', 'btn btn--cta discovery-cta', 'Added to Codex');
    cta.addEventListener('click', () => this.dismiss());
    panel.appendChild(cta);

    const wrap = this.el('div');
    wrap.appendChild(backdrop);
    wrap.appendChild(panel);
    return wrap;
  }

  private rewardChip(label: string, value: string): HTMLDivElement {
    const chip = this.el('div', 'discovery-reward-chip');
    chip.appendChild(this.el('span', 'discovery-reward-chip__label mono-label', label));
    chip.appendChild(this.el('span', 'discovery-reward-chip__value mono-stat', value));
    return chip;
  }

  private dismiss(): void {
    if (this.autoDismissTimer !== null) {
      window.clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    this.emit('dismiss', undefined as never);
    this.close();
  }

  private toHex(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`;
  }

  private injectStyles(): void {
    if (document.getElementById('discovery-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'discovery-modal-styles';
    style.textContent = DISCOVERY_STYLES;
    document.head.appendChild(style);
  }
}

const DISCOVERY_STYLES = `
.discovery-modal-root {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
  padding: 24px;
  pointer-events: auto;
}
.discovery-backdrop {
  position: absolute; inset: 0;
  background: rgba(5, 5, 7, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
}
.discovery-panel {
  position: relative;
  padding: 28px 26px 24px;
  max-width: 340px;
  width: 100%;
  border-radius: var(--r-3xl, 20px);
  border: 1px solid rgba(var(--accent-primary-rgb), 0.35);
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  animation: spring-in 420ms var(--ease-spring) both;
  overflow: hidden;
  text-align: center;
}
.discovery-burst {
  position: absolute; inset: 50%;
  width: 200%; height: 200%;
  transform: translate(-50%, -50%) scale(0.3);
  border-radius: 50%;
  border: 2px solid var(--discovery-color, var(--accent-primary));
  opacity: 0.7;
  pointer-events: none;
  animation: discovery-burst 1200ms var(--ease-snap) forwards;
}
.discovery-burst--lag {
  animation-delay: 160ms;
  opacity: 0.45;
}
.discovery-kicker {
  color: var(--discovery-color, var(--accent-primary));
  letter-spacing: 0.28em;
  font-size: 10px;
}
.discovery-glyph {
  width: 80px; height: 80px; border-radius: 50%;
  background: color-mix(in srgb, var(--discovery-color, var(--accent-primary)) 22%, transparent);
  border: 2px solid var(--discovery-color, var(--accent-primary));
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700; font-size: 36px;
  color: var(--discovery-color, var(--accent-primary));
  box-shadow: 0 0 28px -4px var(--discovery-color, var(--accent-primary));
  margin: 4px 0;
}
.discovery-name {
  font-size: 24px;
  letter-spacing: 0.06em;
  margin: 0;
  color: var(--text-primary);
  text-shadow: 0 0 20px rgba(var(--accent-primary-rgb), 0.35);
}
.discovery-meta { color: var(--text-secondary); letter-spacing: 0.18em; }
.discovery-lore {
  font-size: 12px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
  margin: 0;
  max-width: 280px;
}
.discovery-rewards {
  display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;
  margin: 4px 0 2px;
}
.discovery-reward-chip {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  border-radius: var(--r-lg, 10px);
  background: rgba(var(--accent-primary-rgb), 0.12);
  border: 1px solid rgba(var(--accent-primary-rgb), 0.28);
}
.discovery-reward-chip__label { font-size: 9px; color: var(--text-secondary); }
.discovery-reward-chip__value { font-size: 13px; color: var(--text-primary); font-weight: 700; }
.discovery-cta {
  width: 100%;
  margin-top: 4px;
  min-height: 44px;
}
`;
